import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useWhitelistingPausedRedirect,WHITELISTING_PAUSED_HINT } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";

import { useAppDispatch } from "@store/hooks";
import { addToCart, openSubscriptionCheckout } from "@store/reducers/cart";

import { PremiumPill } from "@components/TierPill";
import { PlayerContext } from "@layouts/PlayerLayout/utils";
import { ERoutes } from "@utils/constants";
import { recordMediaEvent } from "@utils/recordMediaEvent";

import { useModalAnimation } from "@/hooks/useModalAnimation";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import { computeSubscriptionPricing, singleLicensePriceForTier } from "@/utils/pricing";

import LicenseTermsModal from "./LicenseTermsModal";
import ModalScrollArea from "./ModalScrollArea";
import {
  AnnualTotal,
  BillingLabel,
  BillingRow,
  BillingSwitch,
  BillingToggleWrap,
  CardSaveBadge,
  CheckIcon,
  CloseButton,
  ComposerName,
  CoverWrapper,
  DesktopBillingRow,
  FreePrice,
  ModalContainer,
  ModalContent,
  ModalHead,
  ModalHeading,
  OtherUseBox,
  OtherUseContent,
  OtherUseTitle,
  Overlay,
  PlanButton,
  PlanButtonLinkSpacer,
  PlanColumn,
  PlanIcon,
  PlanPrice,
  PlansGrid,
  PlansMainRow,
  PlanTitle,
  PlayBtn,
  PremiumDiamond,
  PriceLead,
  PriceUnit,
  SubFeature,
  SubFeatureList,
  TierPill,
  TrackCover,
  TrackCoverFallback,
  TrackHeader,
  TrackInfo,
  TrackName,
  WarningIcon,
  WhitelistingBtn,
  WhitelistingStepper,
  WhitelistingText,
} from "./styles";

type LicenseMode = "single" | "subscription";
type BillingPeriod = "monthly" | "annual";

interface ILicenseModal {
  open: boolean;
  onClose: () => void;
  trackName: string;
  trackId: string;
  trackSlug: string;
  composerName: string;
  coverUrl?: string;
  isFreeUse?: boolean;
  tier?: "free" | "premium";
  /** Single-license price tier of the track (standard | plus). Selects which
      configured single-license price applies. Default "standard". */
  licenseTier?: string;
  wavUpgrade?: boolean;
  /** Write/read the `?license=` URL param while open. Default true (so a deep
      link / refresh re-opens it). The bottom player passes false: it's a global,
      always-mounted player, and writing the param would ALSO trigger the matching
      /music row's modal to open → two stacked modals. */
  syncUrl?: boolean;
}

const LicenseModal: FC<ILicenseModal> = ({
  open,
  onClose,
  trackName,
  trackId,
  trackSlug,
  composerName,
  coverUrl,
  isFreeUse: isFreeUseProp,
  tier: tierProp = "premium",
  licenseTier = "standard",
  wavUpgrade = false,
  syncUrl = true,
}) => {
  const tier = tierProp === "free" ? "free" : "premium";
  const isFreeUse = isFreeUseProp ?? tier === "free";
  const { mounted, closing } = useModalAnimation(open);
  const dispatch = useAppDispatch();
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const playerContext = useContext(PlayerContext);
  const skipUrlSync = useRef(false);

  const [termsOpen, setTermsOpen] = useState(false);
  const [mode, setMode] = useState<LicenseMode>("subscription");
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [addedYoutube, setAddedYoutube] = useState(false);
  const subConfig = useSubscriptionConfig();
  const [premiumChannels, setPremiumChannels] = useState(subConfig.includedChannels);
  const [premiumWhitelistings, setPremiumWhitelistings] = useState(subConfig.includedClaims);
  const [singleVideoCount, setSingleVideoCount] = useState(1);

  /* Sync modal state to URL */
  useEffect(() => {
    if (!open || skipUrlSync.current || !syncUrl) return;
    const params = new URLSearchParams(searchParams);
    params.set("license", trackSlug);
    params.set("mode", mode);
    if (mode === "subscription") {
      params.set("billing", billing);
    } else {
      params.delete("billing");
    }
    setSearchParams(params, { replace: true });
  }, [open, mode, billing, trackSlug, syncUrl]);

  /* Read URL params on open */
  useEffect(() => {
    if (!open) return;
    setAddedYoutube(false);
    if (!syncUrl) {
      // Bottom player: isolated, no URL deep-link — always start on subscription.
      setMode("subscription");
      return;
    }
    const urlMode = searchParams.get("mode") as LicenseMode | null;
    const urlBilling = searchParams.get("billing") as BillingPeriod | null;
    if (urlMode === "single" || urlMode === "subscription") {
      skipUrlSync.current = true;
      setMode(urlMode);
      if (urlBilling === "monthly" || urlBilling === "annual") {
        setBilling(urlBilling);
      }
      setTimeout(() => { skipUrlSync.current = false; }, 0);
    } else {
      setMode("subscription");
    }
  }, [open, trackId]);

  const isPlaying = playerContext?.data?.id === trackId && playerContext?.data?.isPlaying;

  const handleClose = useCallback(() => {
    // Do NOT pause the track on close — the bottom player is global, so a
    // preview started from the modal keeps playing after the modal closes.
    if (syncUrl) {
      const params = new URLSearchParams(searchParams);
      params.delete("license");
      params.delete("mode");
      params.delete("billing");
      setSearchParams(params, { replace: true });
    }
    setSingleVideoCount(1);
    setAddedYoutube(false);
    onClose();
  }, [onClose, searchParams, setSearchParams, syncUrl]);

  /* Escape to close */
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !termsOpen) handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, termsOpen, handleClose]);


  const handleAddYoutube = useCallback(() => {
    // Paused (ToU §17.4): close this modal and route to Account Issues.
    if (whitelistingSuspended) {
      handleClose();
      return goFixWhitelisting();
    }
    dispatch(
      addToCart({
        trackId,
        trackName,
        composerName,
        coverUrl: coverUrl || "",
        licenseType: "youtube-video",
        licenseLabel: "YouTube Video License",
        price: wavUpgrade ? subConfig.wavLicensePrice : singleLicensePriceForTier(subConfig, licenseTier),
        quantity: singleVideoCount,
      }),
    );
    recordMediaEvent(trackId, "track", "cart_add");
    setAddedYoutube(true);
    setTimeout(() => handleClose(), 1000);
  }, [dispatch, trackId, trackName, composerName, coverUrl, tier, singleVideoCount, handleClose, whitelistingSuspended, goFixWhitelisting]);

  /* Straight to the licensing page. There used to be an 800ms wait here so a
     green "added" state could be seen on the box first — but this row is a
     doorway, not something you add to a basket, and colouring it green on the
     way out said otherwise. With the colour gone the wait only delays the
     navigation the click asked for. */
  const handleContact = useCallback(() => {
    onClose();
    navigate(ERoutes.Licensing);
  }, [navigate, onClose]);

  const handlePlay = useCallback(() => {
    if (!playerContext) return;
    const { data, setData, setPlay } = playerContext;
    if (data?.id === trackId && data?.isPlaying) {
      setPlay(false);
    } else {
      setData({ id: trackId, isPlaying: true, slug: trackSlug });
      setPlay(true);
    }
  }, [playerContext, trackId, trackSlug]);

  const handleSubscription = useCallback(() => {
    if (whitelistingSuspended) {
      onClose();
      return goFixWhitelisting();
    }
    onClose();
    dispatch(openSubscriptionCheckout({ channels: premiumChannels, claims: premiumWhitelistings, billingPeriod: billing }));
  }, [onClose, dispatch, premiumChannels, premiumWhitelistings, billing, whitelistingSuspended, goFixWhitelisting]);

  const isAnnual = billing === "annual";
  // Centralized pricing (claims billed per channel) — mirrors the backend.
  const subPricing = computeSubscriptionPricing(subConfig, { channels: premiumChannels, claims: premiumWhitelistings, isAnnual });
  const premiumPrice = subPricing.totalMonthly;

  if (!mounted) return null;

  return createPortal(
    <>
      <Overlay>
        <ModalContainer $closing={closing} onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={handleClose} aria-label="Close">
            &#x2715;
          </CloseButton>
          <ModalHead>
            <ModalHeading>
              Choose Your Option
              {/* A WAV upgrade on a free track is a PAID action, so the "Free"
                  pill would contradict the subscription + paid-license options
                  shown below — hide it in that case (all resolutions). */}
              {!(tier === "free" && wavUpgrade) && (
                <TierPill $variant={tier}>{tier === "free" ? "Free" : "Premium"}</TierPill>
              )}
              {tier === "premium" && (
                <PremiumDiamond>
                  <svg viewBox="0 0 24 24">
                    <path d="M6 3h12l4 6-10 13L2 9Z" />
                  </svg>
                </PremiumDiamond>
              )}
            </ModalHeading>
          </ModalHead>

          <ModalScrollArea>
          <ModalContent>

          {/* ── Track info (scrolls; only the heading + ✕ stay fixed above) ── */}
          <TrackHeader>
            <CoverWrapper>
              {coverUrl ? (
                <TrackCover src={coverUrl} alt={trackName} />
              ) : (
                <TrackCoverFallback>&#9835;</TrackCoverFallback>
              )}
              <PlayBtn onClick={handlePlay}>
                {isPlaying ? (
                  <svg viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" style={{ transform: "translateX(1px)" }}>
                    <polygon points="6 3 20 12 6 21 6 3" />
                  </svg>
                )}
              </PlayBtn>
            </CoverWrapper>
            <TrackInfo>
              <TrackName>{trackName}</TrackName>
              <ComposerName>{composerName}</ComposerName>
            </TrackInfo>
          </TrackHeader>

          <PlansGrid>
            <DesktopBillingRow>
              <BillingToggleWrap>
                <BillingSwitch $annual={billing === "annual"} onClick={() => setBilling(billing === "monthly" ? "annual" : "monthly")} />
                <BillingLabel $active={billing === "annual"} onClick={() => setBilling("annual")}>
                  Annual (Save {subConfig.annualDiscountPercent}%)
                </BillingLabel>
              </BillingToggleWrap>
            </DesktopBillingRow>
            <PlansMainRow>
                {/* Premium Plan */}
                <PlanColumn $highlighted>
                  <CardSaveBadge style={{ position: "absolute", top: "12px", right: "12px" }}>Popular</CardSaveBadge>
                  <PlanIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </PlanIcon>
                  <PlanTitle>Subscription</PlanTitle>

                  <BillingRow>
                    <BillingToggleWrap>
                      <BillingSwitch $annual={billing === "annual"} onClick={() => setBilling(billing === "monthly" ? "annual" : "monthly")} />
                      <BillingLabel $active={billing === "annual"} onClick={() => setBilling("annual")}>
                        Annual (Save {subConfig.annualDiscountPercent}%)
                      </BillingLabel>
                    </BillingToggleWrap>
                  </BillingRow>

                  <PlanPrice>
                    <PriceLead>${premiumPrice.toFixed(2)}<PriceUnit>monthly</PriceUnit></PriceLead>
                    <WhitelistingStepper>
                      <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumChannels(Math.max(1, premiumChannels - 1)); }} style={{ visibility: premiumChannels <= 1 ? "hidden" : "visible" }}>−</WhitelistingBtn>
                      <WhitelistingText>{premiumChannels} YouTube {premiumChannels === 1 ? "channel" : "channels"}</WhitelistingText>
                      <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumChannels(premiumChannels + 1); }}>+</WhitelistingBtn>
                    </WhitelistingStepper>
                    <WhitelistingStepper>
                      <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumWhitelistings(Math.max(subConfig.includedClaims, premiumWhitelistings - subConfig.claimIncrement)); }} style={{ visibility: premiumWhitelistings <= subConfig.includedClaims ? "hidden" : "visible" }}>−</WhitelistingBtn>
                      <WhitelistingText>{premiumWhitelistings} claims per channel</WhitelistingText>
                      <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumWhitelistings(Math.min(200, premiumWhitelistings + subConfig.claimIncrement)); }}>+</WhitelistingBtn>
                    </WhitelistingStepper>
                    {billing === "annual" && (
                      <AnnualTotal style={{ width: "100%", textAlign: "center" }}>${(premiumPrice * 12).toFixed(2)} charged upfront ({subConfig.annualDiscountPercent}% saved)</AnnualTotal>
                    )}
                  </PlanPrice>

                  <SubFeatureList>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Full <PremiumPill $spaced $nudge={-1}>Premium</PremiumPill> access as MP3 + WAV
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Covers {premiumChannels} YouTube {premiumChannels === 1 ? "channel" : "channels"}
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      {premiumWhitelistings} monthly claims per channel{premiumChannels > 1 ? ` (${premiumWhitelistings * premiumChannels} claims total)` : ""}
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Monetization allowed
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      1-line credit text
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Auto-resolve claims
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Cancel anytime
                    </SubFeature>
                    <SubFeature>
                      <WarningIcon>&#10007;</WarningIcon>
                      No music or ambient content
                    </SubFeature>
                    <SubFeature>
                      <WarningIcon>&#10007;</WarningIcon>
                      No use in livestreams
                    </SubFeature>
                  </SubFeatureList>
                  <PlanButton $gold onClick={handleSubscription} title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 3h12l4 6-10 13L2 9Z" />
                    </svg>
                    Get Started
                  </PlanButton>
                  {/* Reserves the same height as the Single card's "View full
                      license terms" link so both plan buttons align (desktop). */}
                  <PlanButtonLinkSpacer aria-hidden>View full license terms</PlanButtonLinkSpacer>
                </PlanColumn>

                <PlanColumn $added={addedYoutube}>
                  <PlanIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </PlanIcon>
                  <PlanTitle>{"YouTube Video License"}</PlanTitle>
                  <PlanPrice $single>
                    {isFreeUse && !wavUpgrade ? (
                      <FreePrice>Free</FreePrice>
                    ) : (
                      <PriceLead>
                        {`$${(wavUpgrade ? subConfig.wavLicensePrice : singleLicensePriceForTier(subConfig, licenseTier)) * singleVideoCount}`}
                        <PriceUnit>one-time</PriceUnit>
                      </PriceLead>
                    )}
                    <WhitelistingStepper style={{ justifyContent: "center" }}>
                      <WhitelistingText>Any YouTube Channel</WhitelistingText>
                    </WhitelistingStepper>
                    {isFreeUse && !wavUpgrade ? (
                      <WhitelistingStepper style={{ justifyContent: "center" }}>
                        <WhitelistingText>Use in any video</WhitelistingText>
                      </WhitelistingStepper>
                    ) : (
                      <WhitelistingStepper>
                        <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setSingleVideoCount(Math.max(1, singleVideoCount - 1)); }} style={{ visibility: singleVideoCount <= 1 ? "hidden" : "visible" }}>−</WhitelistingBtn>
                        <WhitelistingText>{singleVideoCount} {singleVideoCount === 1 ? "claim" : "claims"}</WhitelistingText>
                        <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setSingleVideoCount(singleVideoCount + 1); }}>+</WhitelistingBtn>
                      </WhitelistingStepper>
                    )}
                  </PlanPrice>
                  <SubFeatureList>
                    {isFreeUse && !wavUpgrade ? (
                      <SubFeature>
                        <CheckIcon>&#10003;</CheckIcon>
                        Use "{trackName}" in any YouTube video
                      </SubFeature>
                    ) : (
                      <SubFeature>
                        <CheckIcon>&#10003;</CheckIcon>
                        License "{trackName}" for {singleVideoCount} YouTube {singleVideoCount === 1 ? "video" : "videos"}
                      </SubFeature>
                    )}
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      Monetization allowed
                    </SubFeature>
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      {isFreeUse && !wavUpgrade ? "MP3" : "MP3 + WAV"}
                    </SubFeature>
                    {/* The pricing page lists this for the same plan; the modal
                        omitted it, so the two described the licence differently. */}
                    {!(isFreeUse && !wavUpgrade) && (
                      <SubFeature>
                        <CheckIcon>&#10003;</CheckIcon>
                        1-line credit text
                      </SubFeature>
                    )}
                    {isFreeUse && !wavUpgrade && (
                      <SubFeature>
                        <CheckIcon>&#10003;</CheckIcon>
                        Extended credit text required
                      </SubFeature>
                    )}
                    <SubFeature>
                      <CheckIcon>&#10003;</CheckIcon>
                      100% human-made music
                    </SubFeature>
                    <SubFeature>
                      <WarningIcon>&#10007;</WarningIcon>
                      No music or ambient content
                    </SubFeature>
                    <SubFeature>
                      <WarningIcon>&#10007;</WarningIcon>
                      No use in livestreams
                    </SubFeature>
                  </SubFeatureList>
                  {isFreeUse && !wavUpgrade ? (
                    <PlanButton onClick={handleClose}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download Track
                    </PlanButton>
                  ) : (
                    <PlanButton $added={addedYoutube} onClick={handleAddYoutube} title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}>
                      {addedYoutube ? (
                        <>&#10003; Added to Cart</>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", transform: "translateY(-1px)" }}>
                            <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
                          </svg>
                          Add to Cart
                        </>
                      )}
                    </PlanButton>
                  )}
                  {/* License terms — readable before adding to cart. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTermsOpen(true); }}
                    style={{
                      background: "none", border: "none", padding: 0, marginTop: 12,
                      font: "inherit", fontSize: "0.8125rem", color: "rgba(235, 194, 142, 0.95)",
                      textDecoration: "underline", textUnderlineOffset: "2px", cursor: "pointer",
                      display: "block", textAlign: "center", width: "100%",
                    }}
                  >
                    View full license terms
                  </button>
                </PlanColumn>
            </PlansMainRow>

            {/* ── Other / Custom (always visible) ── */}
            <OtherUseBox onClick={handleContact}>
              <PlanIcon style={{ width: "36px", height: "36px", marginBottom: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4" />
                  <path d="M19 17V5a2 2 0 0 0-2-2H4" />
                </svg>
              </PlanIcon>
              <OtherUseContent>
                <OtherUseTitle>Have any other use case?</OtherUseTitle>
              </OtherUseContent>
              <button
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  fontFamily: "Raleway, sans-serif",
                  background: "transparent",
                  border: "1px solid rgba(235, 194, 142, 0.3)",
                  color: "#f3d0a4",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(235, 194, 142, 0.08)";
                  e.currentTarget.style.color = "#ffdca8";
                  e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#f3d0a4";
                  e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.3)";
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 6l-10 7L2 6" />
                </svg>
                License Request
              </button>
            </OtherUseBox>
          </PlansGrid>
          </ModalContent>
          </ModalScrollArea>
        </ModalContainer>
      </Overlay>

      <LicenseTermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </>,
    document.body,
  );
};

export default LicenseModal;
